import { cn } from "@/lib/utils";
import { splitCaptionByQuery } from "@/lib/caption-highlight";
import type { LiveCaptionLine } from "@/hooks/use-caption-window";

/**
 * The line being spoken, rendered inline in the control bar under the player.
 *
 * It used to float over the video, which caused two problems worth not
 * repeating: it covered the picture (and the player's own control strip), and
 * the video bleeding through a translucent plate made it hard to read. In the
 * control block it costs a single row of layout and never fights the embed for
 * space.
 *
 * **One line, in full, centred.** It used to be a lyrics track — the current
 * line sharp in the middle, its neighbours blurred above and below, the strip
 * sliding 52px per line inside a fixed 156px box. That spent three quarters of
 * the space on lines you are not hearing, and clamped the one line worth
 * reading to two, so a long sentence arrived cut in half. The neighbours are
 * gone and the line is never clamped: the block grows to fit what is said.
 */

export interface LiveCaptionProps {
    /** The transcript lines around the playhead, in order. */
    lines?: LiveCaptionLine[];
    /** Index into `lines` of the line being spoken; -1 when there is none. */
    activeIndex: number;
    /** The search term, highlighted the same way it is in the result rows. */
    query: string;
    className?: string;
}

export function LiveCaption({
    lines = [],
    activeIndex,
    query,
    className,
}: LiveCaptionProps) {
    const active = activeIndex >= 0 ? lines[activeIndex] : undefined;

    return (
        <div
            data-live-caption=""
            className={cn(
                // One line's worth of height, reserved so the transport buttons
                // do not jump the moment playback starts. A longer line grows
                // the block instead of being clamped.
                "relative mx-auto min-h-[1.5em] w-full max-w-4xl px-1 text-center",
                className,
            )}
            // The result list already announces the matched line, and this
            // changes several times a second — reading it aloud would be
            // unusable with a screen reader.
            aria-hidden="true"
        >
            {active ? (
                <p className="text-balance text-base font-medium leading-snug text-foreground sm:text-lg">
                    {splitCaptionByQuery(active.text, query).map((segment, index) =>
                        segment.match ? (
                            <mark
                                key={index}
                                className={cn(
                                    // Strong on purpose: this is the word the learner
                                    // searched for, in the sentence they are hearing.
                                    "rounded-[3px] bg-amber-300 px-1 font-semibold text-stone-950",
                                    "box-decoration-clone",
                                    "dark:bg-amber-400 dark:text-stone-950",
                                )}
                            >
                                {segment.text}
                            </mark>
                        ) : (
                            <span key={index}>{segment.text}</span>
                        ),
                    )}
                </p>
            ) : null}
        </div>
    );
}
